import {
  createDocumentAssemblerTemplateVersion,
  DOCUMENT_ASSEMBLER_LIMITS,
  replaceDocumentAssemblerTemplateVersionFile,
  useDocumentAssemblerTemplateVersions,
} from '@commenda/api';
import {
  Alert,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Label,
  Textarea,
} from '@commenda/kakashi';
import {
  messageFromApiErrorResponse,
  readJsonResponseAs,
} from '@commenda/utils';
import { useMemo, useState } from 'react';
import { useNotification } from '../hooks/useNotification';

import { DocumentAssemblerDocxField } from './DocumentAssemblerDocxField';

const UPLOAD_FAILED_MESSAGE = 'The file could not be uploaded. Try again.';

interface DocumentAssemblerVersionUploadDialogProps {
  templateId: string;
  /** When set the dialog replaces that version's file instead of creating a new version. */
  versionIdToReplace?: string;
  onClose: () => void;
  onUploaded: (versionId: string | null) => void;
}

export const DocumentAssemblerVersionUploadDialog = ({
  templateId,
  versionIdToReplace,
  onClose,
  onUploaded,
}: DocumentAssemblerVersionUploadDialogProps) => {
  const { setNotification } = useNotification();

  const isReplacing = Boolean(versionIdToReplace);

  // Replacing a file leaves the conventions alone; only a new version inherits them.
  const { templateVersions } = useDocumentAssemblerTemplateVersions(
    isReplacing ? null : templateId
  );

  const [file, setFile] = useState<File | null>(null);
  /** Null until the author edits the box, so a version list that resolves after the
   *  dialog opened still prefills — state seeded once, on the first render, would not. */
  const [editedAuthorGuidance, setEditedAuthorGuidance] = useState<
    string | null
  >(null);
  const [isUploading, setIsUploading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // The response promises no order, so the version to inherit from is found by number.
  const newestVersion = useMemo(
    () =>
      [...(templateVersions ?? [])].sort((a, b) => b.version - a.version)[0],
    [templateVersions]
  );

  // An author who clears the box sends '', which the server records as no conventions.
  const authorGuidance =
    editedAuthorGuidance ?? newestVersion?.authorGuidance ?? '';

  const onSubmit = async () => {
    if (!file) return;

    setSubmitError(null);
    setIsUploading(true);

    try {
      const response = versionIdToReplace
        ? await replaceDocumentAssemblerTemplateVersionFile(
            templateId,
            versionIdToReplace,
            file
          )
        : await createDocumentAssemblerTemplateVersion(templateId, {
            file,
            authorGuidance,
          });

      if (!response.ok) {
        // The file is kept selected: every refusal here is one the author can act on and retry.
        setSubmitError(
          await messageFromApiErrorResponse(response, UPLOAD_FAILED_MESSAGE)
        );
        return;
      }

      setNotification({
        show: true,
        valid: true,
        title: isReplacing
          ? 'File replaced — the problems were re-checked'
          : 'Version uploaded',
      });

      if (isReplacing) {
        onUploaded(null);
        onClose();
        return;
      }

      const body = await readJsonResponseAs<{
        templateVersion: { id: string };
      }>(response);
      onUploaded(body?.templateVersion?.id ?? null);
      onClose();
    } catch {
      // A rejected fetch never produced a response to explain itself.
      setSubmitError(UPLOAD_FAILED_MESSAGE);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader className="flex-col items-start gap-xs">
          <DialogTitle>
            {isReplacing ? 'Replace this version’s file' : 'Upload a version'}
          </DialogTitle>
          <DialogDescription>
            {isReplacing
              ? 'The new file is read straight away. The problem list, and whether this version can be used, are recomputed from it. The writing conventions are left untouched.'
              : 'The upload always succeeds. Whatever is wrong with the file is shown on the next screen, all of it at once.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-xl p-3xl">
          {submitError ? (
            <Alert variant="destructive" description={submitError} />
          ) : null}

          <FieldWrapper>
            <Label required>Word file</Label>
            <DocumentAssemblerDocxField
              file={file}
              onFileChange={setFile}
              disabled={isUploading}
            />
          </FieldWrapper>

          {/* Deliberately absent when replacing: the replace endpoint refuses a multipart body
              carrying any field but the file. */}
          {isReplacing ? null : (
            <FieldWrapper>
              <Label htmlFor="document-assembler-author-guidance" optional>
                Writing conventions
              </Label>
              <Textarea
                id="document-assembler-author-guidance"
                rows={5}
                value={authorGuidance}
                maxLength={DOCUMENT_ASSEMBLER_LIMITS.maxAuthorGuidanceLength}
                placeholder="How defined terms are styled, tense, how the parties are referred to, date and percentage formats…"
                onChange={(event) =>
                  setEditedAuthorGuidance(event.target.value)
                }
              />
              <p className="txt-extra-small-regular mt-xs text-text-secondary">
                {newestVersion?.authorGuidance ? (
                  <>
                    Carried over from v{newestVersion.version}. Clearing the box
                    records “no writing conventions” for the new version rather
                    than falling back to it.{' '}
                  </>
                ) : null}
                Prose that governs how the whole document reads. You can edit it
                while this version is a draft; publishing freezes it, and after
                that the only way to change it is a new version. It never
                affects whether the file can be used.
              </p>
            </FieldWrapper>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary" size="sm" type="button">
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="primary"
            size="sm"
            type="button"
            disabled={!file || isUploading}
            isLoading={isUploading}
            loadingLabel="Reading the file…"
            onClick={() => {
              onSubmit().catch(() => undefined);
            }}
          >
            {isReplacing ? 'Replace file' : 'Upload version'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
